<template>
  <v-menu
    v-model="date_menu"
    :close-on-content-click="false"
    transition="scale-transition"
    max-width="290px"
    min-width="290px"
  >
    <template #activator="{ on, attrs }">
      <v-text-field
        ref="date"
        :value="displayValue"
        readonly
        v-bind="attrs"
        type="text"
        rounded
        :label="computedLabel"
        outlined
        :color="inputColor"
        :dark="inputDark"
        :dense="isDense"
        v-on="on"
      />
    </template>

    <!-- Date Picker -->
    <v-date-picker
      v-if="currentStep === 'date' || currentStep === 'dates'"
      v-model="selectedDates"
      :min="dateMin"
      no-title
      :range="isRange"
      class="taypa-radius"
      @input="onDatesSelected"
    />

    <!-- Time Picker - Para fecha única o hora inicio de rango -->
    <v-time-picker
      v-if="currentStep === 'time' || currentStep === 'start-time'"
      v-model="startTime"
      format="24hr"
      scrollable
      :color="inputColor"
      @click:minute="onStartTimeSelected"
    >
      <v-spacer />
      <v-btn 
        text 
        color="primary" 
        @click="currentStep = isRange ? 'dates' : 'date'"
      >
        Atrás
      </v-btn>
    </v-time-picker>

    <!-- Time Picker - Hora Fin (solo para rango) -->
    <v-time-picker
      v-if="currentStep === 'end-time'"
      v-model="endTime"
      format="24hr"
      scrollable
      :color="inputColor"
      @click:minute="onEndTimeSelected"
    >
      <v-spacer />
      <v-btn text color="primary" @click="currentStep = 'start-time'">
        Atrás
      </v-btn>
    </v-time-picker>
  </v-menu>
</template>

<script>
export default {
  props: {
    value: {
      type: [String, Array],
      default: '',
    },
    dateMin: {
      type: String,
      default: null,
    },
    inputColor: {
      type: String,
      default: 'primary',
    },
    inputDark: {
      type: Boolean,
      default: false,
    },
    isDense: {
      type: Boolean,
      default: false,
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    includeTime: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    date_menu: false,
    currentStep: 'date', // 'date' | 'dates' | 'time' | 'start-time' | 'end-time'
    selectedDates: null,
    startTime: '00:00',
    endTime: '23:59',
  }),

  computed: {
    computedLabel() {
      if (this.includeTime) {
        return this.isRange 
          ? 'Rango de Fecha y Hora' 
          : 'Fecha y Hora'
      }
      return this.isRange 
        ? this.$t('admin.app.date_range') 
        : this.$t('admin.app.date')
    },

    displayValue() {
      if (!this.value) return ''
      
      // Modo Range
      if (this.isRange && Array.isArray(this.value)) {
        if (this.includeTime) {
          // ["2025-10-28T10:00", "2025-10-30T17:00"]
          return this.value.join(' ~ ')
        }
        // ["2025-10-28", "2025-10-30"]
        return this.value.join(' ~ ')
      }
      
      // Modo Single
      if (this.includeTime && typeof this.value === 'string') {
        // "2025-10-28T10:00"
        return this.value
      }
      
      // "2025-10-28"
      return this.value
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return
        
        if (this.isRange && Array.isArray(newVal)) {
          // Modo Range
          if (this.includeTime && newVal.length === 2) {
            const [startDateTime, endDateTime] = newVal
            const [startDate, startTimeStr] = this.parseDateTime(startDateTime)
            const [endDate, endTimeStr] = this.parseDateTime(endDateTime)
            
            this.selectedDates = [startDate, endDate]
            this.startTime = startTimeStr
            this.endTime = endTimeStr
          } else {
            this.selectedDates = newVal
          }
        } else if (!this.isRange && typeof newVal === 'string') {
          // Modo Single
          if (this.includeTime) {
            const [date, time] = this.parseDateTime(newVal)
            this.selectedDates = date
            this.startTime = time
          } else {
            this.selectedDates = newVal
          }
        }
      },
    },

    isRange: {
      immediate: true,
      handler(val) {
        this.currentStep = val ? 'dates' : 'date'
      },
    },
  },

  methods: {
    parseDateTime(dateTimeString) {
      if (!dateTimeString) return ['', '00:00']
      
      if (dateTimeString.includes('T')) {
        return dateTimeString.split('T')
      }
      return [dateTimeString, '00:00']
    },

    onDatesSelected(dates) {
      this.selectedDates = dates

      if (!this.includeTime) {
        // Sin hora: emitir directamente
        this.$emit('input', dates)
        
        // Cerrar si es single o si es range con 2 fechas
        if (!this.isRange || (Array.isArray(dates) && dates.length === 2)) {
          this.date_menu = false
        }
        return
      }

      // Con hora: pasar al time picker
      if (this.isRange) {
        // Esperar a que se seleccionen 2 fechas
        if (Array.isArray(dates) && dates.length === 2) {
          this.currentStep = 'start-time'
        }
      } else {
        // Fecha única: pasar directamente al time picker
        this.currentStep = 'time'
      }
    },

    onStartTimeSelected() {
      if (this.isRange) {
        // Rango: ir a seleccionar hora fin
        this.currentStep = 'end-time'
      } else {
        // Fecha única: emitir y cerrar
        const result = `${this.selectedDates}T${this.startTime}`
        this.$emit('input', result)
        this.closeModal()
      }
    },

    onEndTimeSelected() {
      // Solo para rango
      const result = [
        `${this.selectedDates[0]}T${this.startTime}`,
        `${this.selectedDates[1]}T${this.endTime}`,
      ]

      this.$emit('input', result)
      this.closeModal()
    },

    closeModal() {
      this.date_menu = false
      this.currentStep = this.isRange ? 'dates' : 'date'
    },
  },
}
</script>

<style scoped>
.taypa-radius {
  border-radius: 16px;
}
</style>